/**
 * Application chrome — sidebar, topbar, toast host, global FABs. Classified
 * as the `shell` ESLint element (not `shared`); feature imports must be
 * added to the `shell` allow-list in eslint.config.js with a reason.
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { motion, useReducedMotion } from 'motion/react'
import { NotificationCenter } from './notification-center'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Toaster } from 'sonner'
import {
  LayoutDashboard,
  Calendar,
  ClipboardList,
  LayoutGrid,
  Users,
  UserCog,
  CreditCard,
  Map,
  Wallet,
  ShoppingBag,
  Tags,
  Settings,
  Sparkles,
  Rocket,
  RefreshCw,
  BarChart3,
  Dumbbell,
  Trophy,
  History,
  Bell,
  CircleHelp,
  Building2,
  Activity,
  KeyRound,
  LogOut,
  MailCheck,
  Menu,
  ChevronLeft,
  Info,
  Search,
  Plus,
  Sun,
  Moon,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { HelpContextProvider, HelpPanel, useHelpShortcut } from './help-panel'
import { useSignOut } from '@/features/auth/hooks/use-sign-out'
import { useAccountModals } from '@/features/auth/components/account-modals'
import { useAuthStore } from '@/core/auth/auth-store'
import { useAppReady } from '@/shared/hooks/use-app-ready'
import { useCurrentRole } from '@/features/staff/hooks/use-current-role'
import type { RoleTier } from '@/features/staff/domain/role-gating'
import type { ClubPermission } from '@/core/models/staff'
import { useCurrentClubStore } from '@/core/auth/current-club-store'
import { useClub } from '@/features/settings/hooks/use-club'
import { coachRoleLive, crmLive, posLive, seriesLive, tournamentsLive } from '@/core/models/club'
import { useUserClubs } from '@/features/auth/hooks/use-user-clubs'
import { useCourts } from '@/features/courts/hooks/use-courts'
import { ExpiredSubscriptionBanner } from '@/features/settings/components/expired-banner'
import { InstallSidebarPill } from '@/features/install/components/install-sidebar-pill'
import { TrialCountdownChip } from '@/features/settings/components/trial-countdown-chip'
import { AppStatusBanners } from './app-status-banners'
import { Tooltip } from '@/shared/ui/tooltip'
import { Skeleton } from '@/shared/ui'
import { PreviewBanner } from '@/features/preview-session/components/preview-banner'
import { PreviewWelcomeDialog } from '@/features/preview-session/components/preview-welcome-dialog'
import { WelcomeDialog } from './welcome-dialog'
import { usePreviewSession } from '@/features/preview-session/hooks/use-preview-session'
import { usePreviewScreenTrail } from '@/features/preview-session/hooks/use-preview-screen-trail'
import { useVersionAutoReload } from '@/core/version/use-version-auto-reload'
import { useIsPlatformAdmin } from '@/core/auth/use-is-admin'
import { helpFor, type HelpRoleTier } from '@/core/help/help-registry'
import { useFcmAutoRegister } from '@/core/fcm/use-fcm-register'
import { useFcmForeground } from '@/core/fcm/use-fcm-foreground'
import { useFcmNavigation } from '@/core/fcm/use-fcm-navigation'
import { useTrackClubActivity } from '@/features/admin/hooks/use-track-club-activity'
import { CommandPalette, useCommandPaletteShortcut, type CommandAction } from './command-palette'
// These four overlays render only on demand (quick-sale / quick-booking /
// feedback / admin theme-lab) and are never needed at first paint. Lazy-
// loading them keeps the heavy BookingFormPanel + SalePanel out of the main
// entry chunk (they stay in the shared route chunks that already pull them),
// shrinking first-paint download. Each render site is wrapped in a single
// <Suspense fallback={null}> — a null fallback is correct: nothing should
// flash before a deliberately-triggered overlay mounts.
const FeedbackDialog = lazy(() =>
  import('@/features/feedback/components/feedback-dialog').then((m) => ({
    default: m.FeedbackDialog,
  })),
)
const SalePanel = lazy(() =>
  import('@/features/sales/components/sale-panel').then((m) => ({
    default: m.SalePanel,
  })),
)
import { useQuickSaleStore } from '@/features/sales/hooks/use-quick-sale-store'
import { ActionDock } from './action-dock'
import { useNotificationCenterStore } from './notification-center-store'
const BookingFormPanel = lazy(() =>
  import('@/features/bookings/components/booking-form-panel').then((m) => ({
    default: m.BookingFormPanel,
  })),
)
import { useQuickBookingStore } from '@/features/bookings/hooks/use-quick-booking-store'
const ThemeLabOverlay = lazy(() =>
  import('@/shared/theme-lab/components/theme-lab-overlay').then((m) => ({
    default: m.ThemeLabOverlay,
  })),
)
import { useApplyThemeOverrides, useThemeLabShortcut } from '@/shared/theme-lab/apply'
import { useApplyClubBrand } from '@/shared/theme/use-apply-club-brand'
import { setTheme, getStoredTheme, type ThemeName } from '@/shared/theme/theme'
import { logger } from '@/core/logger'
import {
  setAnalyticsClub,
  setAnalyticsUser,
  trackPageView,
} from '@/core/analytics'
import { setSentryClub, setSentryPlatformAdmin, setSentryRole, setSentryUser } from '@/core/sentry'
import { formatLongDate, formatShortDate } from '@/shared/utils/format'

/**
 * Viewport match hook — returns `true` when the media query is currently
 * satisfied. Used by the shell for drawer-vs-expanded layout switches.
 * Runs SSR-safely (window only touched inside useEffect/useState init).
 */
function useMatchMedia(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  )
  useEffect(() => {
    const mql = window.matchMedia(query)
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches)
    setMatches(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [query])
  return matches
}

/**
 * Clear legacy GA4 user/club identity whenever the operational account changes.
 * The analytics compatibility helpers intentionally never send either ID.
 */
function useAnalyticsSync(): void {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  const clubId = useCurrentClubStore((s) => s.clubId)
  useEffect(() => {
    setAnalyticsUser(uid)
  }, [uid])
  useEffect(() => {
    setAnalyticsClub(clubId)
  }, [clubId])
}

/**
 * Mirror auth + club + role onto every Sentry event so triage can filter
 * the issue list by tenant / role / subscription status. Cleared on
 * sign-out + on club switch (the new club's tags overwrite the old).
 *
 * Kept separate from `useAnalyticsSync` because the Sentry call shape
 * differs (tags + context vs user properties) and we read additional club
 * fields (subscription, demo flag) that GA4 doesn't need.
 */
function useSentrySync(): void {
  const user = useAuthStore((s) => s.user)
  const clubId = useCurrentClubStore((s) => s.clubId)
  const clubQuery = useClub(clubId)
  const club = clubQuery.data
  const role = useCurrentRole()
  const staffRoleId = role.staffRole?.id ?? null

  useEffect(() => {
    if (user) {
      setSentryUser({
        uid: user.uid,
        email: user.email ?? null,
        displayName: user.displayName ?? null,
      })
    } else {
      setSentryUser(null)
    }
  }, [user])

  useEffect(() => {
    if (!club) {
      setSentryClub(null)
      return
    }
    setSentryClub({
      id: club.id,
      name: club.name,
      slug: club.slug,
      subscriptionStatus: club.status,
      billingStatus: club.billingStatus,
      partnerClub: club.subscription?.partnerClub ?? false,
      isDemo: club.isDemo ?? false,
      activeCourtCount: club.activeCourtCount ?? 0,
    })
  }, [club])

  useEffect(() => {
    setSentryRole(staffRoleId)
  }, [staffRoleId])

  const { isAdmin } = useIsPlatformAdmin()
  useEffect(() => {
    setSentryPlatformAdmin(isAdmin)
  }, [isAdmin])
}

export interface NavItem {
  path: string
  label: string
  icon: LucideIcon
  count?: number
  /** Minimum role tier required to see this item. Defaults to 'staff' (everyone). */
  requires?: RoleTier
  /**
   * Gate on a specific permission instead of (or in addition to) a tier.
   * Use this when nav visibility must track an owner-revocable permission
   * rather than a coarse role tier — e.g. Ingresos (`viewIngresos`), which
   * reception holds by default but the owner can revoke. Keeps the rail link
   * in lockstep with the route's `gatedByPermission(...)` guard so a user
   * never gets a dead link or a hidden-but-reachable page.
   */
  requiresPermission?: ClubPermission
  /** Platform-admin-only items. Hidden from every staff/manager/owner regardless of tier. */
  adminOnly?: boolean
  /**
   * Gate on a club feature being live in addition to the tier/permission.
   * Used by the coach report, which only exists when `coachRoleLive(club)`
   * (both gate layers on) — hides the rail link when the feature is dark so a
   * user never lands on the "feature not active" empty state.
   */
  requiresCoachRole?: boolean
  /** Gate on `tournamentsLive(club)` — hides the Torneos link when dark. */
  requiresTournaments?: boolean
  /**
   * Club Módulos progressive disclosure (`docs/PLAN-clubmanager-pwa.md` M2).
   * Gate on `posLive(club)` — hides Ventas/Catálogo when the owner has
   * turned the POS módulo off in Ajustes → Módulos.
   */
  requiresPos?: boolean
  /** Gate on `seriesLive(club)` — hides Recurrentes when the Series módulo is off. */
  requiresSeries?: boolean
  /** Gate on `crmLive(club)` — hides Clientes when the CRM módulo is off. */
  requiresCrm?: boolean
}
interface NavGroup {
  section: string
  items: NavItem[]
}

/**
 * Sidebar hierarchy — three stable buckets, admission rules (see
 * docs/screen-role-matrix.md → "Hierarchy rules"):
 *
 *   Día a día — transactional surface used during a shift (Agente primary)
 *   Gestión   — resource / schedule / audit touched weekly (Gerente primary)
 *   Club      — configuration touched monthly or less (Dueño primary),
 *               grows via tabs rather than new sidebar items
 *
 * Per-item role requirements: staff-tier is implicit (no `requires`); items
 * that should be hidden from Agente declare `requires: 'manager'` or
 * `'owner'`. Platform admins bypass filtering entirely in `<Sidebar>`.
 *
 * Role-permission matrix (see also: role-gating.ts):
 *
 *   dashboard / bookings / calendar / members  — all staff
 *   series / activity                          — manager+ (matrix §7, §9)
 *   courts / staff                             — manager+ (tier-gated route)
 *   analytics                                  — `viewAnalytics` permission
 *                                                 (default O/M); route is
 *                                                 PermissionGate, nav `requires`
 *                                                 below is the tier-coarse hint
 *   settings (Club, tabbed)                    — manager+ (tab gating inside)
 *
 * Ingresos (formerly Pagos) is a top-level item under Gestión — owner's
 * view of reservation revenue. Producto lives as a Club tab
 * (/settings?tab=producto); the legacy /product route redirects there.
 * The legacy /payments route redirects to /ingresos.
 */
export const NAV: NavGroup[] = [
  {
    section: 'Día a día',
    items: [
      { path: '/', label: 'Hoy', icon: LayoutDashboard },
      // View-permission gated (owner-revocable in Personal → Permisos por rol)
      // so the rail link tracks the route guard. Defaults grant these to every
      // role, so default behaviour is unchanged.
      { path: '/calendar', label: 'Calendario', icon: Calendar, requiresPermission: 'viewCalendar' },
      { path: '/bookings', label: 'Reservas', icon: ClipboardList },
      // Recurrentes is operational for receptionists too — they manage
      // ongoing academy/lesson rosters. Matches the secondary-role 'A'
      // line in screen-role-matrix.md §7. Módulo gate: hides when the
      // owner has switched Series off (M2 progressive disclosure).
      { path: '/series', label: 'Recurrentes', icon: RefreshCw, requiresSeries: true },
      { path: '/members', label: 'Clientes', icon: Users, requiresPermission: 'viewCrm', requiresCrm: true },
      // Ventas: a desk task El Agente does every shift (ring up balls /
      // grips / water). Primary role = reception, so it lives in Día a
      // día — NOT Gestión. The owner reads the aggregate in Ingresos
      // (Gestión); *creating* a sale is operational. requires:'staff'
      // so receptionists see it. (spec/clubmanager/15 — primary role El Agente.)
      // Módulo gate: hides when the owner has switched POS/Ventas off.
      { path: '/ventas', label: 'Ventas', icon: ShoppingBag, requires: 'staff', requiresPos: true },
      { path: '/activity', label: 'Actividad', icon: History, requiresPermission: 'viewActivityLog' },
    ],
  },
  {
    section: 'Gestión',
    items: [
      { path: '/courts', label: 'Pistas', icon: Map, requires: 'manager' },
      // Catálogo: what the club *sells* (config), next to Pistas (what
      // it *rents*). Tags icon — priced catalogue, distinct from Ventas'
      // ShoppingBag (the transactional surface that consumes it). Same
      // POS módulo gate as Ventas — the registry bundles them together.
      { path: '/catalogo', label: 'Catálogo', icon: Tags, requires: 'manager', requiresPos: true },
      // Personal: distinct icon (UserCog) so it doesn't visually collide
      // with "Jugadores" (Users) above. Same Users-icon pattern would
      // make scanning the sidebar slower.
      { path: '/staff', label: 'Personal', icon: UserCog, requires: 'manager' },
      // Permission-gated, not tier-gated: reception holds `viewIngresos` by
      // default (cash reconciliation) but the owner can revoke it in Ajustes.
      // The rail link must mirror the route guard, not the coarse role tier.
      { path: '/ingresos', label: 'Ingresos', icon: Wallet, requiresPermission: 'viewIngresos' },
      // Rendimiento: chart icon, not lightning bolt — it's a stats
      // dashboard, not a notification surface.
      { path: '/analytics', label: 'Rendimiento', icon: BarChart3, requires: 'manager' },
      // Entrenadores: per-coach report (#101). Manager+ AND the coach role
      // must be live for this club (dark by default).
      { path: '/coaches', label: 'Entrenadores', icon: Dumbbell, requires: 'manager', requiresCoachRole: true },
      { path: '/tournaments', label: 'Torneos', icon: Trophy, requiresPermission: 'createBooking', requiresTournaments: true },
      // "Producto", not "Novedades": the screen is the roadmap (what the club
      // can do today, what's next, where to vote). "Novedades" is the CHANGELOG
      // modal that opens from the version row inside it — the menu using the
      // same word gave one screen two names and stole the changelog's
      // (2026-08 design pass, S4).
      { path: '/product', label: 'Producto', icon: Rocket, requires: 'manager' },
      { path: '/settings', label: 'Ajustes', icon: Settings, requires: 'manager' },
    ],
  },
  // Admin section removed from the club sidebar — admins reach the
  // admin surface via the user-menu shortcut "Admin · Clubes" (see
  // `buildUserMenuExtras` below) and via the brand chip in AdminShell
  // itself. Keeping the entry in the club sidebar duplicated navigation
  // and visually mixed two surfaces that are intentionally separate.
]

const CRUMBS: Record<string, string> = {
  '/': 'Hoy',
  '/bookings': 'Reservas',
  '/series': 'Recurrentes',
  '/calendar': 'Calendario',
  '/activity': 'Actividad',
  '/courts': 'Pistas',
  '/members': 'Clientes',
  '/events': 'Eventos',

  '/analytics': 'Rendimiento',
  '/coaches': 'Entrenadores',
  '/tournaments': 'Torneos',
  '/staff': 'Personal',
  '/ingresos': 'Ingresos',
  '/ventas': 'Ventas',
  '/catalogo': 'Catálogo',
  '/product': 'Producto',
  '/settings': 'Ajustes',
  // Note: `/admin/*` lives in its own AdminShell — separate chrome,
  // separate breadcrumb. Not listed here.
}

/**
 * Resolve a pathname to its base CRUMBS/help route by walking up parent
 * segments until one matches. A nested detail route (e.g. `/tournaments/{id}`)
 * isn't a CRUMBS key, so without this the header title fell back to "Inicio"
 * and the "Ayuda" button vanished on the tournament detail page (2026-07
 * rescore). `/tournaments/{id}` → `/tournaments` → "Torneos" + its help.
 */
function resolveBasePath(pathname: string): string {
  let p = pathname
  while (p && p !== '/' && !(p in CRUMBS)) {
    p = p.slice(0, p.lastIndexOf('/')) || '/'
  }
  return p in CRUMBS ? p : pathname
}

const SIDEBAR_KEY = 'cm.sidebar'

export function AppShell() {
  // Hold the static splash until the active club doc has loaded (or errored).
  // Two payoffs: the shell paints with the real club name instead of flashing
  // the "Mi club" fallback, and the first authenticated Firestore read has
  // already succeeded before child screens fire their day-data queries — which
  // closes the preview first-paint permission race that surfaced the
  // "No pudimos cargar los datos del día" banner.
  const activeClubId = useCurrentClubStore((s) => s.clubId)
  const activeClubQuery = useClub(activeClubId)
  const activeClub = activeClubQuery.data
  useAppReady(!!activeClub || activeClubQuery.isError)
  // Club Módulos (M2) — drives the command-palette's Ventas/Recurrentes/
  // Clientes entries so ⌘K stays in lockstep with the sidebar.
  const posOn = posLive(activeClub)
  const seriesOn = seriesLive(activeClub)
  const crmOn = crmLive(activeClub)
  useFcmAutoRegister()
  useFcmForeground()
  useFcmNavigation()
  useAnalyticsSync()
  useSentrySync()
  useVersionAutoReload()
  useTrackClubActivity()
  useApplyThemeOverrides()
  const signOut = useSignOut()
  // Apply the active club's brand accent (--accent + soft + hover) on every
  // club switch. Reuses the `activeClub` query above (shared TanStack cache),
  // so no extra Firestore round-trip.
  useApplyClubBrand(activeClub?.brand?.accentColor)
  const { isAdmin } = useIsPlatformAdmin()
  useThemeLabShortcut(isAdmin)
  // Membership count for the "Cambiar de club" gate — palette item + user
  // menu item hide when there's nowhere to switch to.
  const authUser = useAuthStore((s) => s.user)
  const userClubsQuery = useUserClubs(authUser)
  const canSwitchClub = isAdmin || (userClubsQuery.data?.length ?? 0) >= 2
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState<boolean>(() => localStorage.getItem(SIDEBAR_KEY) === 'collapsed')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  useCommandPaletteShortcut(setPaletteOpen)
  // Global counter-sale (spec/clubmanager/15 BR-17) — topbar ＋Venta + ⌘K both open
  // the one shared SalePanel in create mode, mounted once here.
  const quickSaleOpen = useQuickSaleStore((s) => s.open)
  const openQuickSale = useQuickSaleStore((s) => s.openQuickSale)
  const closeQuickSale = useQuickSaleStore((s) => s.closeQuickSale)
  // Novedades + Feedback folded into the dock (spec/clubmanager/15 BR-17): the
  // composition root owns the count + openers and injects them.
  const novedadesUnread = useNotificationCenterStore((s) => s.pending)
  const openNovedades = useNotificationCenterStore((s) => s.openCenter)
  const closeNovedades = useNotificationCenterStore((s) => s.closeCenter)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  // Global new-booking (spec/clubmanager/15 BR-17): dock "Reserva" + ⌘K open the
  // one shared BookingFormPanel in create mode from anywhere — parity
  // with quick-sale (was: just navigate to Hoy, which never opened the
  // sidebar). Seed mirrors the Reservas "Nueva reserva" button: no
  // court (panel snaps to the first available), next clock hour clamped
  // to 08–23. Fresh each open.
  const quickBookingOpen = useQuickBookingStore((s) => s.open)
  const openQuickBooking = useQuickBookingStore((s) => s.openQuickBooking)
  const closeQuickBooking = useQuickBookingStore((s) => s.closeQuickBooking)

  // Primary overlays are mutually exclusive. The right-side booking + sale
  // SidePanels render ABOVE the bottom-centre Novedades panel; with both open
  // the drawer covers Novedades' close (×) button and traps it — only Escape
  // clears it, and that also dismisses the panel the operator actually wanted.
  // Each opener dismisses the other two so at most one transient overlay shows.
  const handleNewBooking = useCallback(() => {
    closeNovedades()
    closeQuickSale()
    openQuickBooking()
  }, [closeNovedades, closeQuickSale, openQuickBooking])
  const handleQuickSale = useCallback(() => {
    closeNovedades()
    closeQuickBooking()
    openQuickSale()
  }, [closeNovedades, closeQuickBooking, openQuickSale])
  const handleNovedades = useCallback(() => {
    closeQuickBooking()
    closeQuickSale()
    openNovedades()
  }, [closeQuickBooking, closeQuickSale, openNovedades])
  // Recompute the seed `startDateTime` each time the panel opens so the
  // suggested slot reflects the *current* hour, not the hour at app boot.
  // The dependency on `quickBookingOpen` is intentional — when the panel
  // is dismissed we want the next open to refresh, even though the panel
  // doesn't read the seed when closed. Marked exhaustive-deps-disabled
  // because eslint flags the dep as unused (it IS unused inside the
  // closure body; the dependency is what triggers the recompute).
  const quickBookingSeed = useMemo(() => {
    const start = new Date()
    start.setHours(Math.max(8, Math.min(23, start.getHours())), 0, 0, 0)
    return { courtId: '', startDateTime: start }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickBookingOpen])
  const isPreviewVisitor = usePreviewSession().active
  const isMobile = useMatchMedia('(max-width: 767px)')
  const location = useLocation()
  // Record which screens a preview visitor views (the admin "Recorrido"). No-op
  // for real users; gated on the preview session.
  usePreviewScreenTrail(activeClubId, isPreviewVisitor, location.pathname)
  const role = useCurrentRole()
  const helpContent = useMemo(
    () => helpFor(resolveBasePath(location.pathname), (role.tier ?? null) as HelpRoleTier | null),
    [location.pathname, role.tier],
  )
  // Active help context — defaults to the URL `?tab=` value so any screen
  // already keyed on tabs gets context-aware help for free. Components
  // without URL state can layer `<HelpContextOverride>` deeper in the tree.
  // Settings accepts label-slug aliases in the URL (`perfil`→`club`,
  // `preferencias`→`policies`, `facturacion`→`subscription`); the help pages
  // are keyed on the internal ids, so map the aliases here or a deep-linked
  // `?tab=preferencias` would open the wrong help page.
  const helpContextId = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('tab')
    if (!raw) return null
    const HELP_TAB_ALIASES: Record<string, string> = {
      perfil: 'club',
      marca: 'club',
      facturacion: 'subscription',
      preferencias: 'policies',
    }
    return HELP_TAB_ALIASES[raw] ?? raw
  }, [location.search])
  useHelpShortcut(helpContent ? () => setHelpOpen(true) : null)

  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, collapsed ? 'collapsed' : 'expanded')
  }, [collapsed])

  // Close the mobile drawer whenever the route changes so tapping a nav item
  // doesn't leave the overlay stuck open.
  useEffect(() => {
    if (drawerOpen) setDrawerOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // Analytics — every route change fires a `page_view` event that carries the
  // current `club_id` (auto-injected by the analytics module). Title comes
  // from the document so operators can slice by "Reservas" / "Jugadores"…
  useEffect(() => {
    const path = `${location.pathname}${location.search ?? ''}`
    trackPageView(path, document.title)
  }, [location.pathname, location.search])

  // Close on Escape; prevent body scroll while the drawer is open on mobile.
  useEffect(() => {
    if (!drawerOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false)
    }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [drawerOpen])

  const sidebarCls = collapsed ? 'sidebar-icons' : ''
  const drawerCls = drawerOpen ? 'sidebar-open' : ''

  return (
    <HelpContextProvider id={helpContextId}>
    <div className={`app density-compact ${sidebarCls} ${drawerCls}`.trim()}>
      <Brand collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
      <Topbar
        isMobile={isMobile}
        onHamburger={() => setDrawerOpen(true)}
        onOpenHelp={helpContent ? () => setHelpOpen(true) : null}
      />
      <Sidebar onNavigate={() => setDrawerOpen(false)} collapsed={collapsed} />
      {/* Mobile nav-drawer scrim — a REAL element (not a `::before`) so a tap on
          the dimmed area outside the drawer actually closes it. The old
          `.app.sidebar-open::before` pseudo-scrim couldn't hold a click handler,
          and the fallback `onClick` on `<main>` never fired because the pseudo
          sat on top of it. */}
      {drawerOpen && (
        <div
          className="sidebar-scrim"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}
      <main className="main">
        <PreviewBanner />
        <PreviewWelcomeDialog />
        <WelcomeDialog />
        <AppStatusBanners />
        <ExpiredSubscriptionBanner />
        <RouteTransition />
      </main>
      {helpOpen && helpContent && (
        <HelpPanel content={helpContent} onClose={() => setHelpOpen(false)} />
      )}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        actions={buildCommandActions(navigate, role.tier ?? null, {
          onSignOut: () => {
            signOut()
              .then(() => navigate('/login', { replace: true }))
              .catch((err) => logger.e('sign out failed', err))
          },
          onOpenHelp: helpContent ? () => setHelpOpen(true) : null,
          onQuickSale: isPreviewVisitor || !posOn ? null : handleQuickSale,
          onNewBooking: isPreviewVisitor ? null : handleNewBooking,
          isAdmin,
          canSwitchClub,
          posOn,
          seriesOn,
          crmOn,
        })}
      />
      {/* Lazy overlays — fallback={null} so nothing flashes before a
          deliberately-triggered panel finishes loading its chunk. */}
      <Suspense fallback={null}>
        {quickSaleOpen && <SalePanel onClose={closeQuickSale} />}
        {quickBookingOpen && (
          <BookingFormPanel
            initial={quickBookingSeed}
            onClose={closeQuickBooking}
            onCreated={closeQuickBooking}
          />
        )}
        {feedbackOpen && (
          <FeedbackDialog onClose={() => setFeedbackOpen(false)} />
        )}
      </Suspense>
      {!isPreviewVisitor && (
        <ActionDock
          onQuickSale={posOn ? handleQuickSale : null}
          onNewBooking={handleNewBooking}
          onNovedades={handleNovedades}
          onFeedback={() => setFeedbackOpen(true)}
          novedadesUnread={novedadesUnread}
        />
      )}
      {/* Global toast host. Stacks new messages, auto-dismisses after 4s,
          re-themed to Night Ops via `--normal-*` CSS vars in index.css.
          **Top-centre** (spec/clubmanager/15 BR-17): the bottom-centre zone is now
          the rapid-action dock, so toasts moved up. `offset` clears the
          52px topbar (+ a small gap) so a toast never sits under the
          chrome. Still in the eyeline; the bottom-centre dock is the
          only bottom chrome now (Novedades FAB folded into it). Sonner
          slides natively for the position; the index.css keyframes
          layer a blur/shadow pulse without fighting sonner's transform. */}
      <Toaster
        theme="dark"
        position="top-center"
        offset={68}
        closeButton
        richColors
        visibleToasts={5}
        toastOptions={{ duration: 4000 }}
      />
      <NotificationCenter />
      {isAdmin && (
        <Suspense fallback={null}>
          <ThemeLabOverlay />
        </Suspense>
      )}
    </div>
    </HelpContextProvider>
  )
}

/**
 * Build the command palette's action list. Role-filtered: managers get the
 * business actions, receptionists get only ops. Kept in-shell so navigation
 * targets stay synchronised with the sidebar.
 */
interface PaletteExtras {
  onSignOut: () => void
  onOpenHelp: (() => void) | null
  /** Opens the global counter-sale panel (spec/clubmanager/15 BR-17). Null for
   *  preview visitors (they can't sell). */
  onQuickSale: (() => void) | null
  /** Opens the global new-booking panel (spec/clubmanager/15 BR-17). Null for
   *  preview visitors. */
  onNewBooking: (() => void) | null
  isAdmin: boolean
  /** True if the current user can reach more than one club (admin: every
   * club; staff: ≥ 2 memberships). Drives the "Cambiar de club" palette
   * entry — hidden for single-club operators since the picker auto-bounces
   * them back. */
  canSwitchClub: boolean
  /** Club Módulos (M2) — hides Ventas/Nueva venta, Recurrentes and Clientes
   *  from the palette when the owner has turned the respective módulo off. */
  posOn: boolean
  seriesOn: boolean
  crmOn: boolean
}

function buildCommandActions(
  navigate: (to: string) => void,
  tier: RoleTier | null,
  extras: PaletteExtras,
): CommandAction[] {
  const can = (required: RoleTier) => {
    if (!tier) return true // admin / loading — don't hide
    if (required === 'staff') return true
    if (required === 'manager') return tier === 'manager' || tier === 'owner'
    return tier === 'owner'
  }

  const actions: CommandAction[] = []

  // Día a día (all roles)
  actions.push(
    {
      id: 'nav-hoy',
      label: 'Ir a Hoy',
      icon: LayoutDashboard,
      group: 'Día a día',
      keywords: ['inicio', 'ops', 'home'],
      onSelect: () => navigate('/'),
    },
    {
      id: 'nav-calendar',
      label: 'Calendario',
      icon: Calendar,
      group: 'Día a día',
      keywords: ['agenda', 'semana', 'mes', 'dia'],
      onSelect: () => navigate('/calendar'),
    },
    {
      id: 'nav-bookings',
      label: 'Reservas',
      icon: ClipboardList,
      group: 'Día a día',
      keywords: ['listado', 'lista'],
      onSelect: () => navigate('/bookings'),
    },
    {
      id: 'nav-activity',
      label: 'Actividad',
      icon: Bell,
      group: 'Día a día',
      keywords: ['log', 'audit', 'eventos'],
      onSelect: () => navigate('/activity'),
    },
  )
  // Módulo gates (M2) — Clientes/Ventas hide from the palette in lockstep
  // with the sidebar when the owner has turned CRM/POS off.
  if (extras.crmOn) {
    actions.push({
      id: 'nav-members',
      label: 'Clientes',
      icon: Users,
      group: 'Día a día',
      keywords: ['crm', 'jugadores', 'socios', 'players', 'grupos', 'clases'],
      onSelect: () => navigate('/members'),
    })
  }
  if (extras.posOn) {
    actions.push({
      id: 'nav-ventas',
      label: 'Ventas',
      icon: ShoppingBag,
      group: 'Día a día',
      keywords: ['venta', 'bolas', 'grip', 'agua', 'extras', 'mostrador', 'tienda', 'cobrar producto'],
      onSelect: () => navigate('/ventas'),
    })
  }

  if (can('manager')) {
    actions.push(
      ...(extras.seriesOn
        ? [
            {
              id: 'nav-series',
              label: 'Recurrentes',
              icon: RefreshCw,
              group: 'Gestión',
              keywords: ['series', 'clases', 'recurring'],
              onSelect: () => navigate('/series'),
            } as CommandAction,
          ]
        : []),
      {
        id: 'nav-courts',
        label: 'Pistas',
        icon: Map,
        group: 'Gestión',
        keywords: ['canchas', 'courts'],
        onSelect: () => navigate('/courts'),
      },
      {
        id: 'nav-staff',
        label: 'Personal',
        icon: Users,
        group: 'Gestión',
        keywords: ['staff', 'team', 'empleados'],
        onSelect: () => navigate('/staff'),
      },

      {
        id: 'nav-ingresos',
        label: 'Ingresos',
        icon: Wallet,
        group: 'Gestión',
        keywords: ['pagos', 'cobros', 'revenue', 'dinero', 'caja', 'por cobrar'],
        onSelect: () => navigate('/ingresos'),
      },
      {
        id: 'nav-analytics',
        label: 'Rendimiento',
        icon: LayoutDashboard,
        group: 'Gestión',
        keywords: ['analiticas', 'metricas', 'stats', 'kpi', 'rendimiento'],
        onSelect: () => navigate('/analytics'),
      },
      {
        id: 'nav-settings',
        label: 'Club',
        icon: Settings,
        group: 'Club',
        keywords: ['ajustes', 'config', 'perfil', 'politicas', 'horario'],
        onSelect: () => navigate('/settings'),
      },
      {
        id: 'nav-producto',
        label: 'Producto',
        icon: Sparkles,
        group: 'Club',
        keywords: ['feedback', 'roadmap', 'votar', 'funcionalidades'],
        hint: 'Club · tab',
        onSelect: () => navigate('/settings?tab=producto'),
      },
    )
  }

  // Owner-only billing shortcuts. Gerente can hit /settings but the
  // Facturación + Suscripción tabs (and their deep-link commands) stay
  // hidden — mirrors the `manageBilling` permission gate in settings-page.
  if (can('owner')) {
    actions.push(
      {
        id: 'nav-facturacion',
        label: 'Facturación',
        icon: CreditCard,
        group: 'Club',
        keywords: ['plan', 'suscripcion', 'factura', 'invoice', 'upadelmatch'],
        hint: 'Club · Suscripción · facturas de tu plan',
        onSelect: () => navigate('/settings?tab=subscription#facturacion'),
      },
      {
        id: 'nav-subscription',
        label: 'Suscripción',
        icon: CreditCard,
        group: 'Club',
        keywords: ['plan', 'billing', 'cuota'],
        hint: 'Club · tab',
        onSelect: () => navigate('/settings?tab=subscription'),
      },
    )
  }

  // Quick actions (all roles — the booking form itself gates via data).
  actions.push(
    {
      id: 'action-new-booking',
      label: 'Nueva reserva',
      icon: Plus,
      group: 'Acciones',
      keywords: ['crear', 'walk-in', 'reservar', 'cita'],
      // Opens the shared BookingFormPanel inline (parity with Nueva
      // venta); preview can't book → fall back to Hoy.
      onSelect: extras.onNewBooking ?? (() => navigate('/')),
    },
  )
  if (extras.onQuickSale && extras.posOn) {
    actions.push({
      id: 'action-new-sale',
      label: 'Nueva venta',
      icon: ShoppingBag,
      group: 'Acciones',
      keywords: [
        'venta',
        'cobrar',
        'mostrador',
        'bar',
        'bolas',
        'agua',
        'grip',
        'producto',
        'extra',
      ],
      onSelect: extras.onQuickSale,
    })
  }
  actions.push(
    {
      id: 'action-find-player',
      label: 'Buscar jugador',
      icon: Search,
      group: 'Acciones',
      keywords: ['jugador', 'telefono', 'nombre', 'crm'],
      onSelect: () => navigate('/members'),
    },
  )

  // Cuenta — global account / app actions.
  if (extras.onOpenHelp) {
    actions.push({
      id: 'action-open-help',
      label: 'Ayuda de esta pantalla',
      icon: Info,
      group: 'Cuenta',
      keywords: ['help', 'ayuda', 'soporte', 'guia', '?'],
      hint: '?',
      onSelect: extras.onOpenHelp,
    })
  }
  if (extras.canSwitchClub) {
    actions.push({
      id: 'action-switch-club',
      label: 'Cambiar de club',
      icon: LayoutGrid,
      group: 'Cuenta',
      keywords: ['cambiar', 'switch', 'club', 'seleccionar'],
      onSelect: () => navigate('/select-club'),
    })
  }
  actions.push({
    id: 'action-sign-out',
    label: 'Cerrar sesión',
    icon: LogOut,
    group: 'Cuenta',
    keywords: ['logout', 'salir', 'desconectar', 'sign out'],
    onSelect: extras.onSignOut,
  })

  if (extras.isAdmin) {
    actions.push(
      {
        id: 'nav-admin',
        label: 'Admin · Clubes',
        icon: Building2,
        group: 'Cuenta',
        keywords: ['admin', 'platform', 'impersonate', 'support', 'clubes'],
        onSelect: () => navigate('/admin/clubes'),
      },
      {
        id: 'nav-admin-salud',
        label: 'Admin · Salud',
        icon: Activity,
        group: 'Cuenta',
        // Drift / data-integrity is now a panel inside Salud, so its
        // keywords live here (searching "drift" still lands on Salud).
        keywords: [
          'admin', 'health', 'salud', 'diagnostico', 'version', 'system',
          'drift', 'schema', 'esquema', 'lenient', 'sentry', 'integridad',
        ],
        onSelect: () => navigate('/admin/salud'),
      },
    )
  }

  return actions
}

function Brand({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const clubId = useCurrentClubStore((s) => s.clubId)
  const clubQuery = useClub(clubId)
  const courtsQuery = useCourts(clubId, { activeOnly: true })

  // Never paint the "Mi club" placeholder name: until the club doc resolves,
  // show a neutral skeleton (the splash already holds first paint, so this is
  // the belt-and-suspenders for club switches / cache refetches).
  const loaded = !!clubQuery.data
  const name = clubQuery.data?.name?.trim() || ''
  const chip = loaded ? initialsOf(name) : ''
  const courtCount = courtsQuery.data?.length ?? 0
  const sub =
    courtsQuery.isLoading || !clubQuery.data
      ? 'Club'
      : courtCount === 0
      ? 'Club · sin pistas'
      : `Club · ${courtCount} ${courtCount === 1 ? 'pista' : 'pistas'}`

  return (
    <Tooltip content={collapsed ? 'Expandir menú' : 'Colapsar menú'}>
      <button
        className="brand"
        onClick={onToggle}
        aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
        aria-expanded={!collapsed}
      >
        <div className="brand-chip" aria-hidden="true">
          {loaded ? chip : <Skeleton width={18} height={18} />}
        </div>
        <div className="brand-meta">
          <div className="brand-name">{loaded ? name : <Skeleton width={92} height={13} />}</div>
          <div className="brand-sub mono">{sub}</div>
        </div>
        <ChevronLeft size={14} className="brand-caret" />
      </button>
    </Tooltip>
  )
}

/**
 * Two-letter initials for the brand chip. "Padel Mochis" → "PM",
 * "Centro Deportivo Granada" → "CG". Single-word clubs take the first two chars.
 */
function initialsOf(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return '??'
  const parts = trimmed.split(/\s+/).filter(Boolean)
  const [first, second] = parts
  if (first && second) {
    return (first.charAt(0) + second.charAt(0)).toUpperCase()
  }
  return trimmed.slice(0, 2).toUpperCase()
}

/** Everything `isNavItemVisible` needs to decide one item — no React, no
 *  Firestore, so the sidebar's visibility rules are unit-testable without
 *  mounting the shell. */
export interface NavVisibilityContext {
  isAdmin: boolean
  previewActive: boolean
  /** `!role.isLoading && role.tier != null` — false while the role lookup is in flight. */
  gateReady: boolean
  roleCan: (permission: ClubPermission) => boolean
  roleMeets: (required: RoleTier) => boolean
  coachLive: boolean
  tournamentsOn: boolean
  /** Club Módulos (M2) — Ventas/Catálogo, Recurrentes and Clientes. */
  posOn: boolean
  seriesOn: boolean
  crmOn: boolean
}

/**
 * True when a sidebar/command-palette nav item should show for the given
 * context. Three bypasses of the per-item role requirement:
 *   1. Platform admins (email on `platform_config/admins`) always see every
 *      item. They impersonate clubs for support / debugging and need the
 *      full menu regardless of what staff doc (if any) they hold on the
 *      current club.
 *   2. Preview-token sessions (signed in via `redeem_preview_token`) see
 *      every screen so prospects can evaluate the product. Writes are
 *      blocked at the Firestore-rules layer (`hasPreviewClaim()` denial),
 *      via the `body.preview-mode` CSS gate, and via the capture-phase
 *      form-submit interceptor in `usePreviewSession`.
 *   3. Fail-open while the role lookup is in flight — we let the per-route
 *      `<RoleGate>` redirect anyone who tries to navigate somewhere they
 *      shouldn't see. Avoids flashing Negocio items off the rail on first
 *      load and keeps menus visible for legacy memberships that haven't
 *      seeded a role doc yet.
 */
export function isNavItemVisible(item: NavItem, ctx: NavVisibilityContext): boolean {
  if (item.adminOnly) return ctx.isAdmin
  const gated =
    item.requires != null ||
    item.requiresPermission != null ||
    item.requiresCoachRole === true ||
    item.requiresTournaments === true ||
    item.requiresPos === true ||
    item.requiresSeries === true ||
    item.requiresCrm === true
  if (!gated) return true
  if (ctx.isAdmin) return true
  // Módulo gates bind preview sessions too, so they sit ABOVE the preview
  // bypass. A maqueta is the pitch: whatever the club has switched off is
  // off in the demo as well. Leaving preview above this line let a prospect
  // read "Torneos · Desactivado" on the Módulos screen while the rail still
  // offered them a Torneos link — the same screen contradicting itself.
  // Ordering is the whole fix; the checks are unchanged, and for non-preview
  // users the outcome is identical (a gated item with its módulo off was
  // already hidden, whether by these lines or by the `gateReady` guard).
  if (item.requiresCoachRole && !ctx.coachLive) return false
  if (item.requiresTournaments && !ctx.tournamentsOn) return false
  // Club Módulos (M2) — hide Ventas/Catálogo, Recurrentes and Clientes when
  // the owner has turned the módulo off.
  if (item.requiresPos && !ctx.posOn) return false
  if (item.requiresSeries && !ctx.seriesOn) return false
  if (item.requiresCrm && !ctx.crmOn) return false
  // The rest of the preview bypass stays. A preview user holds no staff doc,
  // so `gateReady` is false and every role/permission check below would fail
  // — applying them would empty the rail and break the demo outright.
  if (ctx.previewActive) return true
  // While role is still loading, default to HIDING gated items. Previous
  // behavior (show-all-on-loading) caused a role-bleed flash: receptionists
  // saw owner-tier items (Ingresos / Rendimiento / Novedades / Ajustes) for a
  // moment on cold load, then the rail collapsed. Hiding by default mirrors
  // the safer-restrict pattern + the `RoleGate` loader behavior.
  if (!ctx.gateReady) return false
  // Permission gate (if declared) AND tier gate (if declared) must both
  // pass. Ingresos uses the permission gate so its rail link tracks the
  // owner-revocable `viewIngresos` exactly like its route guard.
  if (item.requiresPermission && !ctx.roleCan(item.requiresPermission)) return false
  if (item.requires && !ctx.roleMeets(item.requires)) return false
  return true
}

function Sidebar({ onNavigate, collapsed }: { onNavigate?: () => void; collapsed?: boolean }) {
  const role = useCurrentRole()
  const { isAdmin } = useIsPlatformAdmin()
  const preview = usePreviewSession()
  const clubId = useCurrentClubStore((s) => s.clubId)
  const clubQuery = useClub(clubId)
  const coachLive = coachRoleLive(clubQuery.data)
  const tournamentsOn = tournamentsLive(clubQuery.data)
  // Club Módulos progressive disclosure (M2) — Ventas/Catálogo, Recurrentes
  // and Clientes hide when the owner has switched their módulo off.
  const posOn = posLive(clubQuery.data)
  const seriesOn = seriesLive(clubQuery.data)
  const crmOn = crmLive(clubQuery.data)
  const visibleNav = useMemo(() => {
    const gateReady = !role.isLoading && role.tier != null
    const ctx: NavVisibilityContext = {
      isAdmin,
      previewActive: preview.active,
      gateReady,
      roleCan: role.can,
      roleMeets: role.meets,
      coachLive,
      tournamentsOn,
      posOn,
      seriesOn,
      crmOn,
    }
    const groups = NAV.map((group) => ({
      ...group,
      items: group.items.filter((item) => isNavItemVisible(item, ctx)),
    })).filter((group) => group.items.length > 0)
    return groups
  }, [role, isAdmin, preview.active, coachLive, tournamentsOn, posOn, seriesOn, crmOn])

  return (
    <nav className="sidebar" aria-label="Navegación principal">
      {visibleNav.map((group) => (
        <div key={group.section} className="group">
          <div className="group-label">{group.section}</div>
          {group.items.map((item) => (
            <Tooltip key={item.path} content={item.label} side="right" disabled={!collapsed}>
              <NavLink
                to={item.path}
                end={item.path === '/'}
                // RR7 NavLink: when `children` is a render-prop AND `className`
                // is a function, RR7 stringifies the function and writes the
                // source as the literal class attribute (May 2026 incident:
                // collapsed sidebar labels stayed visible because
                // `.app.sidebar-icons .nav-item .label{display:none}` never
                // matched the broken class string). Static class + CSS
                // `[aria-current="page"]` selector for active styling — NavLink
                // already sets that attribute on the matching route.
                className="nav-item"
                onClick={onNavigate}
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span
                        layoutId="sidebar-active-indicator"
                        aria-hidden="true"
                        transition={{ type: 'spring', stiffness: 500, damping: 38 }}
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 6,
                          bottom: 6,
                          width: 3,
                          borderRadius: 2,
                          background: 'var(--accent)',
                        }}
                      />
                    )}
                    {(() => { const NavIcon = item.icon; return <NavIcon size={16} /> })()}
                    <span className="label">{item.label}</span>
                    {item.count != null && <span className="count">{item.count}</span>}
                  </>
                )}
              </NavLink>
            </Tooltip>
          ))}
        </div>
      ))}
      <InstallSidebarPill />
      {/* Sign-out moved out of the operator sidebar 2026-05-14 — it's
       *  reachable from the avatar/UserMenu dropdown (top-right). Real
       *  operators rarely sign out mid-shift; the dedicated button was
       *  visual noise pushing nav items above the fold. Admin shell
       *  keeps its own Salir at the bottom — admins switch sessions
       *  much more often. */}
      <SidebarLegal />
    </nav>
  )
}

/**
 * Legal-links footer at the bottom of the sidebar. Anchors to the marketing
 * site's authoritative pages (LSSI-CE / GDPR / ePrivacy require these to be
 * one click away from anywhere a user might create or modify personal data).
 * Hidden when the sidebar is collapsed to icon-only mode (`.sidebar-icons`)
 * to keep the rail clean — content moves to the user-menu in that mode.
 */
function SidebarLegal() {
  return (
    <div className="sidebar-legal">
      <a
        href="https://upadelmatch.com/privacidad"
        target="_blank"
        rel="noopener noreferrer"
      >
        Privacidad
      </a>
      <span aria-hidden="true">·</span>
      <a
        href="https://upadelmatch.com/terminos"
        target="_blank"
        rel="noopener noreferrer"
      >
        Términos
      </a>
      <span aria-hidden="true">·</span>
      <a
        href="https://upadelmatch.com/cookies"
        target="_blank"
        rel="noopener noreferrer"
      >
        Cookies
      </a>
    </div>
  )
}

/**
 * Route transition — enter-only. The outgoing page swaps instantly; the
 * new content fades in from opacity 0 + 12 px below its final position
 * over 360 ms so it feels like content "settles in" rather than flashing.
 * Enter-only avoids the "empty frame" that an exit animation would
 * introduce on fast nav clicks. Respects prefers-reduced-motion.
 */
function RouteTransition() {
  const location = useLocation()
  const reduced = useReducedMotion()
  if (reduced) return <Outlet />
  return (
    <motion.div
      key={location.pathname}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.36, ease: [0.22, 1, 0.36, 1] }}
      style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      <Outlet />
    </motion.div>
  )
}

function Topbar({
  isMobile,
  onHamburger,
  onOpenHelp,
}: {
  isMobile: boolean
  onHamburger: () => void
  onOpenHelp: (() => void) | null
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(id)
  }, [])

  const crumb = CRUMBS[resolveBasePath(location.pathname)] ?? 'Inicio'
  const hh = String(now.getHours()).padStart(2, '0')
  const mm = String(now.getMinutes()).padStart(2, '0')
  const dateLabel = formatShortDate(now)
  const dateLabelFull = formatLongDate(now)
  // Preview-token visitors have no email/displayName — give them a guest
  // marker instead of the misleading default initials. The user-menu also
  // hides profile / password / delete actions in this mode (handled inside
  // <UserMenu>).
  const previewSession = usePreviewSession()
  // Trial chip is the owner's billing/conversion path — gate it on the same
  // `manageBilling` permission that guards Settings → Suscripción, so reception
  // (and any non-billing role) doesn't see a chip linking to a page they can't open.
  const topbarRole = useCurrentRole()
  const initials = previewSession.active
    ? 'IN'
    : user?.email?.slice(0, 2).toUpperCase() ?? 'CM'

  const signOut = useSignOut()
  const onSignOut = () => {
    signOut()
      .then(() => navigate('/login', { replace: true }))
      .catch((err) => logger.e('sign out failed', err))
  }

  return (
    <header className="topbar" role="banner">
      {isMobile && (
        <button className="tb-hamburger" type="button" onClick={onHamburger} aria-label="Abrir menú">
          <Menu size={20} />
        </button>
      )}
      <div className="tb-crumb">
        <span>{crumb}</span>
        <span className="tb-crumb-dot" aria-hidden="true" />
      </div>
      <div className="tb-center">
        <span className="tb-date mono" aria-label={dateLabelFull}>
          {dateLabel}
        </span>
        <span className="tb-clock mono" aria-label={`Hora actual ${hh}:${mm}`}>
          {hh}
          <span className="tb-clock-sep">:</span>
          {mm}
        </span>
      </div>
      <div className="tb-right">
        {!previewSession.active && topbarRole.can('manageBilling') && <TrialCountdownChip />}
        {onOpenHelp && (
          <button
            className="tb-icon"
            type="button"
            aria-label="Ayuda"
            onClick={onOpenHelp}
          >
            <CircleHelp size={20} />
          </button>
        )}
        <UserMenu
          user={user}
          initials={initials}
          onSignOut={onSignOut}
          isPreview={previewSession.active}
        />
      </div>
    </header>
  )
}

/**
 * User menu — dropdown anchored to the avatar. Shows email + club role
 * (Propietario / Gerente / Recepcionista) + shortcuts for account settings,
 * admin tools (platform admins only), and sign-out. Built on Radix
 * DropdownMenu so keyboard nav + focus + escape are correct.
 *
 * The club role is separate from the platform-admin badge: a user can be both
 * a Recepcionista of a club AND a uPadelMatch platform admin, and we surface
 * both pieces of context so the persona they're operating as is never
 * ambiguous. Previously only the admin chip was shown, which read as the
 * user's role and masked the real staff role during demos.
 */
function UserMenu({
  user,
  initials,
  onSignOut,
  isPreview,
}: {
  user: { email?: string | null; displayName?: string | null } | null
  initials: string
  onSignOut: () => void
  isPreview: boolean
}) {
  const navigate = useNavigate()
  const role = useCurrentRole()
  // `useIsPlatformAdmin` returns `{ isAdmin, isLoading }`; the old code
  // assigned the whole object which is always truthy, so every user saw the
  // admin chip — the root of the persona-review "Maria shows Administrador"
  // report.
  const { isAdmin } = useIsPlatformAdmin()
  // "Cambiar de club" only makes sense when there's somewhere to switch to:
  // a platform admin (every club is reachable) or a staff member belonging
  // to ≥ 2 clubs. Single-club operators see no value in the picker — it
  // bounces them right back here via the auto-select branch in club-gate.
  const authUser = useAuthStore((s) => s.user)
  const userClubsQuery = useUserClubs(authUser)
  const membershipCount = userClubsQuery.data?.length ?? 0
  const canSwitchClub = isAdmin || membershipCount >= 2
  const displayName = isPreview
    ? 'Invitado'
    : user?.displayName ?? user?.email ?? 'Usuario'
  // Display the REAL club role (from staff), not the synthetic `owner`
  // permission-passthrough a platform admin receives. A user has at most one
  // club role per club; if they have none, they are admin-only and only the
  // uPadelMatch admin badge should render.
  //
  // For platform admins, suppress the `owner` staff role specifically: the
  // admin chip already implies owner-equivalent access, and "Propietario +
  // Admin uPadelMatch" reads as redundant/confusing. Non-owner staff roles
  // (recepcionista, manager) still surface so demos retain context.
  const staffRoleId = role.staffRole?.id ?? null
  const roleName =
    role.staffRole && !(isAdmin && staffRoleId === 'owner')
      ? role.staffRole.name
      : null
  const accountModals = useAccountModals()
  const {
    openPassword,
    hasPasswordProvider,
    emailVerified,
    resending,
    resendVerification,
  } = accountModals
  // Self-service account deletion is no longer in this menu — it's
  // relocated to the Personal (/staff) page, gated to users with the
  // right permission. Sign-out is the only terminal action here now.
  const hasMenuActions =
    canSwitchClub ||
    (!isPreview &&
      (hasPasswordProvider ||
        (!emailVerified && Boolean(accountModals.accountEmail))))

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="tb-avatar"
          aria-label={`Mi cuenta · ${displayName}`}
        >
          {initials}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="dropdown-menu"
          style={{
            minWidth: 220,
            background: 'var(--panel)',
            border: '2px solid var(--line)',
            borderRadius: 8,
            padding: 4,
            boxShadow: '0 10px 24px rgba(0, 0, 0, 0.45)',
            zIndex: 100,
            fontSize: 'var(--fs-md)',
            color: 'var(--fg)',
          }}
        >
          <div
            style={{
              padding: '8px 10px 10px',
              borderBottom: '1px solid var(--line)',
              marginBottom: 4,
            }}
          >
            <div style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--fg)' }}>{displayName}</div>
            {user?.email && user.email !== displayName && (
              <div className="mono faint" style={{ fontSize: 'var(--fs-xs)', marginTop: 2 }}>
                {user.email}
              </div>
            )}
            {(roleName || isAdmin) && (
              <div
                style={{
                  marginTop: 6,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {roleName && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: 'var(--panel-2)',
                      border: '2px solid var(--line)',
                      fontSize: 'var(--fs-2xs)',
                      color: 'var(--fg)',
                      fontWeight: 'var(--fw-semibold)',
                      letterSpacing: 'var(--ls-wide)',
                      textTransform: 'uppercase',
                    }}
                  >
                    <Building2 size={10} /> {roleName}
                  </span>
                )}
                {isAdmin && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 'var(--fs-2xs)',
                      color: 'var(--accent)',
                      fontWeight: 'var(--fw-semibold)',
                      letterSpacing: 'var(--ls-wide)',
                      textTransform: 'uppercase',
                    }}
                  >
                    <Sparkles size={10} /> Admin uPadelMatch
                  </span>
                )}
              </div>
            )}
          </div>
          <ThemeSegment />
          <DropdownMenu.Separator
            style={{ height: 1, background: 'var(--line)', margin: '4px 0' }}
          />
          {canSwitchClub && (
            <UserMenuItem icon={LayoutGrid} onSelect={() => navigate('/select-club')}>
              Cambiar de club
            </UserMenuItem>
          )}
          {!isPreview && hasPasswordProvider && (
            <UserMenuItem icon={KeyRound} onSelect={openPassword}>
              Cambiar contraseña
            </UserMenuItem>
          )}
          {!isPreview && !emailVerified && accountModals.accountEmail && (
            <UserMenuItem icon={MailCheck} onSelect={() => void resendVerification()}>
              {resending ? 'Enviando…' : 'Reenviar email de verificación'}
            </UserMenuItem>
          )}
          {hasMenuActions && (
            <DropdownMenu.Separator
              style={{ height: 1, background: 'var(--line)', margin: '4px 0' }}
            />
          )}
          <UserMenuItem icon={LogOut} destructive onSelect={onSignOut}>
            Cerrar sesión
          </UserMenuItem>
          <DropdownMenu.Arrow style={{ fill: 'var(--panel)' }} />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
      {accountModals.render}
    </DropdownMenu.Root>
  )
}

/**
 * ThemeSegment — light/dark toggle inside the user menu. Per-device, persisted
 * via `setTheme()` to localStorage. Distinct from the per-club brand color
 * (which lives in Settings → Marca) because each staff member's eyes differ:
 * one might want light on a brightly-lit reception PC, another dark at night.
 *
 * Internally maps the user-facing labels (Claro / Oscuro) to the theme names
 * (`console` / `night`). The dropdown stays open when the segment is clicked
 * (`onSelect={(e) => e.preventDefault()}`) so the user can preview both
 * before committing.
 */
function ThemeSegment() {
  const [theme, setLocalTheme] = useState<ThemeName>(() => getStoredTheme())
  const apply = (next: ThemeName) => {
    setLocalTheme(next)
    setTheme(next)
  }
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 10px',
      }}
    >
      <span
        style={{
          fontSize: 'var(--fs-2xs)',
          letterSpacing: 'var(--ls-caps)',
          textTransform: 'uppercase',
          color: 'var(--fg-faint)',
          minWidth: 76,
        }}
      >
        Apariencia
      </span>
      <div
        role="radiogroup"
        aria-label="Apariencia"
        style={{
          display: 'inline-flex',
          gap: 0,
          padding: 2,
          background: 'var(--bg-deep)',
          border: '1px solid var(--line)',
          borderRadius: 6,
          marginLeft: 'auto',
        }}
      >
        <ThemeSegmentButton
          icon={Sun}
          label="Claro"
          active={theme === 'console'}
          onSelect={() => apply('console')}
        />
        <ThemeSegmentButton
          icon={Moon}
          label="Oscuro"
          active={theme === 'night'}
          onSelect={() => apply('night')}
        />
      </div>
    </div>
  )
}

function ThemeSegmentButton({
  icon: IconComp,
  label,
  active,
  onSelect,
}: {
  icon: LucideIcon
  label: string
  active: boolean
  onSelect: () => void
}) {
  return (
    <DropdownMenu.Item
      onSelect={(e) => {
        e.preventDefault()
        onSelect()
      }}
      role="radio"
      aria-checked={active}
      aria-label={label}
      style={{
        all: 'unset',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 8px',
        borderRadius: 4,
        cursor: 'pointer',
        fontSize: 'var(--fs-xs)',
        background: active ? 'var(--accent-soft)' : 'transparent',
        color: active ? 'var(--on-accent-soft)' : 'var(--fg-muted)',
        fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-regular)',
      }}
    >
      <IconComp size={12} />
      <span>{label}</span>
    </DropdownMenu.Item>
  )
}

function UserMenuItem({
  icon: IconComp,
  onSelect,
  destructive,
  children,
}: {
  icon: LucideIcon
  onSelect: () => void
  destructive?: boolean
  children: ReactNode
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        borderRadius: 6,
        outline: 'none',
      }}
      className={destructive ? 'user-menu-item danger' : 'user-menu-item'}
    >
      <IconComp size={13} />
      <span style={{ color: destructive ? 'var(--danger)' : 'var(--fg)' }}>{children}</span>
    </DropdownMenu.Item>
  )
}
